import {
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import useChartData from "features/analytics/hooks/useChartData";
import { buildTopPerformingProducts } from "features/products/utils/productUtils";
import { TopPerformingProduct } from "features/products/types";
import {
  buildProductPerformanceChartFields,
  getProductPerformanceTabConfig,
  ProductPerformanceTab,
} from "features/products/utils/productPerformanceTabs";
import { useProducts } from "Hooks/useProducts";
import { useSelectedProject } from "Hooks";
import { useFromToContext } from "Providers/FromToProvider";
import { useMemo } from "react";

const DEFAULT_CAROUSEL_LIMIT = 10;

export function useTopPerformingProducts(
  chartAttributes: ApiChartAttribute[] = [],
  tab: ProductPerformanceTab = "top_sellers",
) {
  const { selectedProject } = useSelectedProject();
  const { chartOutput, chartComparisonOutput } = useFromToContext();
  const { products } = useProducts();
  const tabConfig = useMemo(() => getProductPerformanceTabConfig(tab), [tab]);
  const fetchLimit =
    tabConfig.fetchLimit ?? tabConfig.carouselLimit ?? DEFAULT_CAROUSEL_LIMIT;

  const snapshotChartConfig = useMemo(
    () => ({
      name: "Top Performing Products",
      dimension: [ApiDimension.DIMENSION_PRODUCT_NAME],
      template: ApiChartTemplate.SNAPSHOT,
      type: ApiChartType.TABLE,
      attributes: chartAttributes,
      orderBy: tabConfig.orderBy,
      limit: fetchLimit,
      having: tabConfig.having,
      ...(tabConfig.ranks?.length ? { ranks: tabConfig.ranks } : {}),
      yAxisKeys: [
        {
          key: "l",
          fields: buildProductPerformanceChartFields(tabConfig),
        },
      ],
    }),
    [chartAttributes, fetchLimit, tabConfig],
  );

  const {
    chartData,
    comparisonChartData,
    loading: snapshotLoading,
  } = useChartData(selectedProject, snapshotChartConfig, chartOutput, {
    ignoreModel: true,
    comparisonFrom: tabConfig.useComparison
      ? chartComparisonOutput?.from
      : undefined,
    comparisonTo: tabConfig.useComparison
      ? chartComparisonOutput?.to
      : undefined,
  });

  const products_data: TopPerformingProduct[] = useMemo(() => {
    if (tabConfig.rankByRevenueGrowth && !chartComparisonOutput) {
      return [];
    }

    const rows = chartData?.data || [];
    if (!rows.length) {
      return [];
    }

    let result = buildTopPerformingProducts(
      rows,
      products,
      comparisonChartData?.data || [],
      tabConfig.primaryMetric,
    );

    if (tabConfig.rankByRevenueGrowth) {
      const carouselLimit = tabConfig.carouselLimit ?? DEFAULT_CAROUSEL_LIMIT;
      result = result
        .sort(
          (a, b) =>
            (b.revenueGrowthPercent ?? 0) - (a.revenueGrowthPercent ?? 0),
        )
        .slice(0, carouselLimit)
        .map((product) => ({
          ...product,
          badge: { variant: "rising_star", label: "Rising Star" },
        }));
    } else if (
      tabConfig.primaryMetric === ApiMetric.METRIC_AVERAGE_MARGIN_PERCENT
    ) {
      result = result.map((product, index) =>
        index === 0
          ? {
              ...product,
              badge: { variant: "high_margin", label: "High Margin" },
            }
          : product,
      );
    }

    return result;
  }, [
    chartComparisonOutput,
    chartData,
    comparisonChartData,
    products,
    tabConfig,
  ]);

  return {
    products: products_data,
    loading: snapshotLoading,
    tabConfig,
    needsComparison: Boolean(
      tabConfig.rankByRevenueGrowth && !chartComparisonOutput,
    ),
  };
}
